import { useMemo } from "react";
import { Button } from "@/components/ui/button";

import { Link } from "wouter";
import { ArrowRight, Star, Shield, Clock, Home as HomeIcon, Building2, Briefcase, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { usePageSEO } from "@/hooks/usePageSEO";

import residentialHero from "@/assets/residential-hero.webp";
import commercialHero from "@/assets/commercial-hero.webp";
import franchiseHero from "@/assets/franchise-hero.webp";
import groupImage from "@/assets/group.webp";
import environmentImage from "@/assets/environment-waterfall.webp";

export default function Home() {
    const structuredData = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Select Cleaning",
        "description": "Auckland's trusted professional cleaning service for homes and businesses.",
        "url": "https://selectcleaning.co.nz",
        "telephone": "0800 22 33 15",
        "email": "ajay.l@select.biz",
        "areaServed": {
            "@type": "City",
            "name": "Auckland",
            "addressCountry": "NZ"
        },
        "serviceType": ["Residential Cleaning", "Commercial Cleaning"],
        "priceRange": "$$",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Auckland",
            "addressRegion": "Auckland",
            "addressCountry": "NZ"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "10",
            "bestRating": "5",
            "worstRating": "1"
        },
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Cleaning Services",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Residential House Cleaning",
                        "url": "https://selectcleaning.co.nz/residential"
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Commercial Office Cleaning",
                        "url": "https://selectcleaning.co.nz/commercial"
                    }
                }
            ]
        }
    }), []);

    usePageSEO({
        title: "Select Cleaning | Professional Home & Commercial Cleaning Auckland NZ",
        description: "Select Cleaning — Auckland's trusted professional cleaning service. Residential home cleaning, commercial office cleaning & franchise opportunities. Security-checked cleaners, quality guaranteed.",
        keywords: "cleaning Auckland, house cleaning Auckland, commercial cleaning Auckland, professional cleaners NZ, cleaning service New Zealand",
        canonicalPath: "/",
        structuredData,
    });
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Hero Section - Split Layout */}
            <section className="relative px-4 pt-12 pb-32 lg:pb-40 bg-gradient-to-br from-blue-50 via-blue-100/50 to-primary/20 text-slate-900 overflow-hidden">
                {/* Abstract Background Shapes */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#42b6e6]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#42b6e6]/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
                </div>

                <div className="container mx-auto relative z-10 text-center max-w-4xl py-20">
                    <motion.h1
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="text-5xl md:text-7xl font-bold mb-6 tracking-tight text-primary drop-shadow-sm"
                    >
                        Select Cleaning
                    </motion.h1>
                    <div className="h-24 md:h-16 mb-12 flex items-center justify-center">
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1.5, delay: 0.5 }}
                            className="text-xl md:text-2xl text-slate-600 font-light"
                        >
                            The trusted name in cleaning. Professional services for your home and business.
                        </motion.p>
                    </div>
                </div>

                {/* 3 Main Options Cards - Overlapping Hero */}
                <div className="container mx-auto px-4 relative z-20 -mb-28 md:-mb-20">
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto"
                    >
                        {/* Residential Card */}
                        <motion.div variants={item} className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                            <Link href="/residential">
                                <a className="absolute inset-0 z-20"><span className="sr-only">View Residential Services</span></a>
                            </Link>
                            <div className="h-48 overflow-hidden relative">
                                <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10"></div>
                                <img src={residentialHero} alt="Professional residential house cleaning service in Auckland" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" width="368" height="244" />
                            </div>
                            <div className="p-8 text-center relative z-10 font-bold">
                                <div className="inline-flex items-center justify-center p-3 rounded-full bg-blue-50 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                                    <HomeIcon className="h-8 w-8" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Residential</h2>
                                <p className="text-slate-500 mb-6">Regular home cleaning by security-checked owner-operators.</p>
                                <Button className="w-full bg-primary text-white hover:bg-primary/90 group-hover:bg-primary/90 transition-colors pointer-events-none py-6 text-lg shadow-md">
                                    View Residential Services
                                </Button>
                            </div>
                        </motion.div>

                        {/* Commercial Card */}
                        <motion.div variants={item} className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                            <Link href="/commercial">
                                <a className="absolute inset-0 z-20"><span className="sr-only">View Commercial Services</span></a>
                            </Link>
                            <div className="h-48 overflow-hidden relative">
                                <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10"></div>
                                <img src={commercialHero} alt="Commercial office cleaning services in Auckland" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" width="368" height="252" />
                            </div>
                            <div className="p-8 text-center relative z-10 font-bold">
                                <div className="inline-flex items-center justify-center p-3 rounded-full bg-blue-50 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                                    <Building2 className="h-8 w-8" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Commercial</h2>
                                <p className="text-slate-500 mb-6">Reliable office and workplace cleaning for your business.</p>
                                <Button className="w-full bg-primary text-white hover:bg-primary/90 group-hover:bg-primary/90 transition-colors pointer-events-none py-6 text-lg shadow-md">
                                    View Commercial Services
                                </Button>
                            </div>
                        </motion.div>

                        {/* Franchise Card */}
                        <motion.div variants={item} className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                            <Link href="/franchise">
                                <a className="absolute inset-0 z-20"><span className="sr-only">Franchise Opportunities</span></a>
                            </Link>
                            <div className="h-48 overflow-hidden relative">
                                <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10"></div>
                                <img src={franchiseHero} alt="Cleaning franchise business opportunity in Auckland" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" width="368" height="220" />
                            </div>
                            <div className="p-8 text-center relative z-10 font-bold">
                                <div className="inline-flex items-center justify-center p-3 rounded-full bg-blue-50 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                                    <Briefcase className="h-8 w-8" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Franchise</h2>
                                <p className="text-slate-500 mb-6">Buy a business with guaranteed work and full training.</p>
                                <Button className="w-full bg-primary text-white hover:bg-primary/90 group-hover:bg-primary/90 transition-colors pointer-events-none py-6 text-lg shadow-md">
                                    Franchise Opportunities
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Trust Indicators Section */}
            <section className="pt-40 md:pt-32 pb-12 md:pb-20 bg-white">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl font-bold text-slate-900">Why New Zealand Trusts Us</h2>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto mb-20">
                        {/* Group Image */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <img src={groupImage} alt="Select Cleaning team of security-checked professional cleaners in Auckland" className="w-full h-auto object-cover" loading="lazy" width="600" height="400" />
                        </motion.div>

                        {/* Features List */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="space-y-8"
                        >
                            <FeatureHorizontal
                                icon={<Shield className="h-8 w-8" />}
                                title="Security Checked"
                                description="Every operator is fully vetted and security checked for your complete peace of mind."
                            />
                            <FeatureHorizontal
                                icon={<Star className="h-8 w-8" />}
                                title="Quality Guaranteed"
                                description="We pride ourselves on high standards. If you're not happy, we'll fix it immediately."
                            />
                            <FeatureHorizontal
                                icon={<Clock className="h-8 w-8" />}
                                title="Reliable Service"
                                description="We value your time. Our teams show up as scheduled, every single time."
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Environment Section */}
            <section className="py-12 md:py-20 bg-slate-50 overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col lg:flex-row items-center gap-16 max-w-6xl mx-auto">
                        {/* Text Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="lg:w-1/2"
                        >
                            <div className="inline-flex items-center justify-center p-3 rounded-full bg-green-100 text-green-600 mb-6">
                                <Leaf className="h-6 w-6" />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900 mb-6">We take responsibility</h2>
                            <div className="prose prose-lg text-slate-600 mb-8">
                                <p className="mb-4">
                                    On average, cleaning companies spend 3-8% of their turnover on cleaning chemicals.
                                    We recognize the negative impact of solvents and petrochemicals on the environment,
                                    and the need to help reduce the contamination of our rivers, streams and oceans.
                                </p>
                                <p>
                                    SelectCleaning does not pretend to be the perfect environmental example, but we have
                                    reviewed our own procedures and use the most appropriate products available.
                                </p>
                            </div>

                            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
                                <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-3">
                                    <span className="p-2 bg-blue-50 text-primary rounded-lg">
                                        <Briefcase className="h-5 w-5" />
                                    </span>
                                    You can have more storage space
                                </h3>
                                <p className="text-slate-600">
                                    Because we bring everything we need with us, you can reclaim your cupboards.
                                    Our equipment is the most modern available, and regularly maintained or replaced as necessary.
                                    This means we can provide the very best clean every time.
                                </p>
                            </div>
                        </motion.div>

                        {/* Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="lg:w-1/2 relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-10"></div>
                                <img
                                    src={environmentImage}
                                    alt="Select Cleaning environmental responsibility — eco-friendly cleaning products and practices"
                                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
                                    width="544" height="689"
                                    loading="lazy"
                                />
                            </div>
                            {/* Decorative Elements */}
                            <div className="absolute -z-10 top-10 -right-10 w-64 h-64 bg-green-100 rounded-full blur-3xl opacity-50"></div>
                            <div className="absolute -z-10 -bottom-10 -left-10 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function FeatureHorizontal({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
    return (
        <div className="flex gap-4 p-4 rounded-xl hover:bg-blue-50/50 transition-colors">
            <div className="shrink-0">
                <div className="p-3 bg-blue-50 text-primary rounded-full">
                    {icon}
                </div>
            </div>
            <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">{title}</h3>
                <p className="text-slate-500 leading-relaxed">{description}</p>
            </div>
        </div>
    );
}
